import React from "react";
import CartContext from "./CartContext";

import {
  Table,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Pagination,
  Spinner,
  Alert,
} from "react-bootstrap";

function buildQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    q.set(k, String(v));
  });
  return q.toString();
}

export default class ProductCatalog extends React.Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);

    this.state = {
      // data
      products: [],
      total: 0,
      page: 1,
      pageSize: 20,

      // filters
      categoryId: "",
      brandId: "",
      minPrice: "",
      maxPrice: "",
      search: "",
      minInStock: 0,

      // lookup options
      categories: [],
      brands: [],

      // UI
      imageSize: 70,
      loading: false,
      error: null,
    };

    this._abortController = null;
    this.tableRef = React.createRef();
  }

  componentDidMount() {
    this._isMounted = true;
    this.GetCategories();
    this.GetBrands();
    this.GetProducts();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.abortFetch();
  }

  abortFetch() {
    if (this._abortController) {
      try {
        this._abortController.abort();
      } catch {}
      this._abortController = null;
    }
  }

  // --- API calls ---
  async GetCategories() {
    try {
      const qs = buildQuery({ storeId: this.context.storeId });
      const response = await fetch(
        process.env.REACT_APP_API + "ProductCatalog/subcategories?" + qs
      );
      const data = await response.json();
      if (response.ok) {
        this.setState({ categories: data || [] });
      }
    } catch (err) {
      console.error("GetCategories error", err);
    }
  }

  async GetBrands() {
    try {
      const qs = buildQuery({ storeId: this.context.storeId });
      const response = await fetch(
        process.env.REACT_APP_API + "ProductCatalog/brands?" + qs
      );
      const data = await response.json();
      if (response.ok) {
        this.setState({ brands: data || [] });
      }
    } catch (err) {
      console.error("GetBrands error", err);
    }
  }

  async GetProducts() {
    this.abortFetch();
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    try {
      const {
        page,
        pageSize,
        categoryId,
        brandId,
        minPrice,
        maxPrice,
        search,
        minInStock,
      } = this.state;

      const qs = buildQuery({
        storeId: this.context.storeId,
        page,
        pageSize,
        categoryId: categoryId || undefined,
        brandId: brandId || undefined,
        minPrice: minPrice || undefined,
        maxPrice: maxPrice || undefined,
        search: search || undefined,
        minInStock: minInStock || 0,
      });

      this.setState({ loading: true, error: null });

      const url = process.env.REACT_APP_API + "ProductCatalog?" + qs;
      console.log("Fetching products:", url);

      const response = await fetch(url, { signal });
      const text = await response.text();

      if (!response.ok) {
        this.setState({
          products: [],
          total: 0,
          loading: false,
          error:
            "Failed to load products: " +
            (response.statusText || response.status),
        });
        return;
      }

      let data;
      try {
        data = text ? JSON.parse(text) : { items: [], totalCount: 0 };
      } catch {
        this.setState({
          products: [],
          total: 0,
          loading: false,
          error: "Invalid JSON returned from server.",
        });
        return;
      }

      const items = data.items || data.Items || [];
      const total = data.totalCount ?? data.TotalCount ?? 0;

      if (this._isMounted) {
        this.setState({
          products: items,
          total: typeof total === "number" ? total : parseInt(total) || 0,
          loading: false,
          error: null,
        });
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        this.setState({
          products: [],
          total: 0,
          loading: false,
          error: "Network error while loading products",
        });
      }
    } finally {
      this._abortController = null;
    }
  }

  // --- UI handlers ---
  applyFilters = () => {
    this.setState({ page: 1 }, () => this.GetProducts());
  };

  clearFilters = () => {
    this.setState(
      {
        categoryId: "",
        brandId: "",
        minPrice: "",
        maxPrice: "",
        search: "",
        minInStock: 0,
        page: 1,
      },
      () => this.GetProducts()
    );
  };

  goToPage = (newPage) => {
    const { pageSize, total } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / pageSize || 1));
    const pageClamped = Math.min(Math.max(1, newPage), totalPages);
    if (pageClamped === this.state.page) return;
    this.setState({ page: pageClamped }, () => this.GetProducts());
  };

  onPageSizeChange = (evt) => {
    const size = parseInt(evt.target.value, 10) || 20;
    this.setState({ pageSize: size, page: 1 }, () => this.GetProducts());
  };

  onFilterChange = (field) => (evt) => {
    this.setState({ [field]: evt.target.value });
  };

  // --- Print only the table ---
  // Print only the table using a hidden iframe (works when popups are blocked)
  printTable = () => {
    if (!this.tableRef.current) return;

    try {
      const tableNode = this.tableRef.current;
      const clone = tableNode.cloneNode(true);

      // Fix <img> src values in clone using data-filename attribute
      const imgs = clone.querySelectorAll("img[data-filename]");
      imgs.forEach((img) => {
        const fname = img.getAttribute("data-filename");
        if (fname) {
          img.setAttribute("src", this.buildBlobUrl(fname));
        }
      });

      const headerHtml = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
        <div>${new Date().toLocaleString()}</div>
        <h3 style="margin:0;">Product Catalog</h3>
        <div></div>
      </div>`;

      const html = `
      <html>
        <head>
          <title>Product Catalog</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; padding: 10px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; vertical-align: middle; }
            th { background: #f5f5f5; font-weight: 600; }
            img { display: block; }
          </style>
        </head>
        <body>
          ${headerHtml}
          ${clone.outerHTML}
        </body>
      </html>
    `;

      // create a hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) {
        // fallback to popup (may be blocked) — but we already attempted iframe; give user feedback
        alert(
          "Unable to create print frame. Please allow popups or try a different browser."
        );
        document.body.removeChild(iframe);
        return;
      }

      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      // Wait for images inside iframe to load before printing
      const win = iframe.contentWindow;
      const maxWait = 5000; // ms
      const start = Date.now();

      const tryPrint = () => {
        const imgs = win.document.images;
        if (!imgs || imgs.length === 0) {
          // no images to wait for — print immediately
          win.focus();
          win.print();
          cleanup();
          return;
        }

        let loaded = 0;
        for (let i = 0; i < imgs.length; i++) {
          // If already complete, count immediately
          if (imgs[i].complete) {
            loaded++;
            continue;
          }
          imgs[i].addEventListener("load", () => {
            loaded++;
            if (loaded >= imgs.length) {
              win.focus();
              win.print();
              cleanup();
            }
          });
          imgs[i].addEventListener("error", () => {
            // count error as loaded to avoid hanging
            loaded++;
            if (loaded >= imgs.length) {
              win.focus();
              win.print();
              cleanup();
            }
          });
        }

        // If all were already complete
        if (loaded >= imgs.length) {
          win.focus();
          win.print();
          cleanup();
          return;
        }

        // fallback: if images didn't finish loading within maxWait, print anyway
        const elapsed = Date.now() - start;
        if (elapsed < maxWait) {
          setTimeout(tryPrint, 200);
        } else {
          try {
            win.focus();
            win.print();
          } catch (e) {
            // ignore
          }
          cleanup();
        }
      };

      const cleanup = () => {
        try {
          // small delay to ensure print dialog opened
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 500);
        } catch (e) {
          // ignore
        }
      };

      // start trying to print after a short delay (allow DOM to settle)
      setTimeout(tryPrint, 200);
    } catch (err) {
      console.error("printTable failed:", err);
      alert("Print failed. See console for details.");
    }
  };

  // --- Helpers ---
  renderPagination() {
    const { page, pageSize, total } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / pageSize || 1));
    return (
      <Pagination className="mb-0">
        <Pagination.First
          onClick={() => this.goToPage(1)}
          disabled={page === 1}
        />
        <Pagination.Prev
          onClick={() => this.goToPage(page - 1)}
          disabled={page === 1}
        />
        <Pagination.Item active>{page}</Pagination.Item>
        <Pagination.Next
          onClick={() => this.goToPage(page + 1)}
          disabled={page === totalPages}
        />
        <Pagination.Last
          onClick={() => this.goToPage(totalPages)}
          disabled={page === totalPages}
        />
      </Pagination>
    );
  }

  buildBlobUrl(fileName, folder = "images") {
    if (!fileName) return "";
    const store = this.context && this.context.store;
    if (!store) return "";

    const base = (store.storageBlobUrl || "").trim();
    const container = (store.storageBlobContainerName || "").trim();
    let sas = (store.storageSasToken || "").trim();

    const baseWithSlash = base.endsWith("/") ? base : base + "/";
    const containerClean = container.replace(/^\/+|\/+$/g, "");
    const folderClean = String(folder).replace(/^\/+|\/+$/g, "");
    const file = encodeURIComponent(fileName);

    if (sas && !sas.startsWith("?")) sas = "?" + sas;
    const baseFinal = baseWithSlash.startsWith("http")
      ? baseWithSlash
      : "https://" + baseWithSlash;

    return `${baseFinal}${containerClean}/${folderClean}/${file}${sas}`;
  }

  // --- Render ---
  render() {
    const {
      products,
      page,
      pageSize,
      total,
      categoryId,
      brandId,
      minPrice,
      maxPrice,
      search,
      minInStock,
      categories,
      brands,
      imageSize,
      loading,
      error,
    } = this.state;

    const showingFrom =
      total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total);
    const showingTo = Math.min(page * pageSize, total);

    return (
      <div>
        {/* Filters */}
        <Row className="mb-3">
          <Col md={2}>
            <Form.Group controlId="filterCategory">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={categoryId}
                onChange={this.onFilterChange("categoryId")}
              >
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group controlId="filterBrand">
              <Form.Label>Brand</Form.Label>
              <Form.Select
                value={brandId}
                onChange={this.onFilterChange("brandId")}
              >
                <option value="">All</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Label>Min InStock</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={minInStock}
              onChange={this.onFilterChange("minInStock")}
            />
          </Col>

          <Col md={2}>
            <Form.Label>Price Range</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="Min"
                value={minPrice}
                onChange={this.onFilterChange("minPrice")}
                type="number"
                min="0"
              />
              <Form.Control
                placeholder="Max"
                value={maxPrice}
                onChange={this.onFilterChange("maxPrice")}
                type="number"
                min="0"
              />
            </InputGroup>
          </Col>

          <Col md={2}>
            <Form.Label>Image Size</Form.Label>
            <Form.Control
              type="number"
              min="20"
              max="200"
              value={imageSize}
              onChange={this.onFilterChange("imageSize")}
            />
          </Col>

          <Col md={2} className="d-flex flex-column">
            <Form.Label>Search</Form.Label>
            <div className="d-flex">
              <Form.Control
                placeholder="Product name"
                value={search}
                onChange={this.onFilterChange("search")}
              />
              <Button
                variant="primary"
                className="ms-2"
                onClick={this.applyFilters}
              >
                Apply
              </Button>
              <Button
                variant="light"
                className="ms-2"
                onClick={this.clearFilters}
              >
                Clear
              </Button>
            </div>
          </Col>
        </Row>

        {/* Print Button */}
        <div className="mb-2 text-end">
          <Button variant="secondary" onClick={this.printTable}>
            Print Table
          </Button>
        </div>

        {/* Table */}
        <Table responsive bordered hover size="sm" ref={this.tableRef}>
          <thead className="table-secondary text-center">
            <tr>
              <th>Image</th>
              <th className="text-start">Product Name</th>
              <th>Category</th>
              <th>InStock Qty</th>
              <th>MRP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  <Spinner animation="border" role="status" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="text-center text-danger">
                  {error}
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">
                  No products found
                </td>
              </tr>
            ) : (
              products.map((p) => (
                <tr key={p.id}>
                  <td
                    style={{ width: 100 }}
                    className="text-center align-middle"
                  >
                    {p.imageFileName ? (
                      <img
                        src={this.buildBlobUrl(p.imageFileName)}
                        alt={p.name}
                        width={imageSize}
                        height={imageSize}
                        style={{ objectFit: "contain" }}
                        data-filename={p.imageFileName}
                      />
                    ) : (
                      <div
                        style={{
                          width: imageSize,
                          height: imageSize,
                          borderRadius: "50%",
                          background: "#f2f2f2",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                        }}
                      >
                        No Img
                      </div>
                    )}
                  </td>
                  <td className="text-start align-middle">
                    <strong>{p.name}</strong>
                  </td>
                  <td className="align-middle">{p.category}</td>
                  <td className="align-middle text-center">{p.inStockQty}</td>
                  <td className="align-middle text-end">
                    {p.mrp !== undefined && p.mrp !== null
                      ? Number(p.mrp).toFixed(2)
                      : ""}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            Showing {showingFrom} - {showingTo} of {total}
          </div>
          <div className="d-flex align-items-center">
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={this.onPageSizeChange}
              className="me-2"
            >
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </Form.Select>
            {this.renderPagination()}
          </div>
        </div>

        {error && (
          <Row className="mt-2">
            <Col>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}
      </div>
    );
  }
}
